import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe, translate} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Button} from 'primeng/button';
import {ConfirmationService} from 'primeng/api';
import {
  ProjectReferenceScoreView,
  ProviderScoresResponse,
  ProviderToolResponse,
  TenderDetailResponse,
  TenderPhaseType,
  TenderStatus,
} from '@frontend/shared-api';
import {
  DirectusService,
  DirectusTranslation,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
  FulfillmentType,
} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {BadgeListItem} from '../../list/badge-list/badge-list.types';
import {TabStatusEnum} from '../../../tabs/tab-status.model';
import {TabManagementService} from '../../../tabs/tab-management.service';
import {logTabRender} from '../../../tabs/tab-render-log.util';
import {QuestionnaireService} from '../../../services/questionnaire.service';
import {SubcriteriaBadgeService} from '../../../services/subcriteria-badge.service';
import {QuestionnaireHeaderComponent} from '../questionnaire-header/questionnaire-header.component';
import {QuestionnaireEc3TabsComponent} from './questionnaire-ec3-tabs/questionnaire-ec3-tabs.component';
import {QuestionnaireStandardTabsComponent} from './questionnaire-standard-tabs/questionnaire-standard-tabs.component';
import {
  AttributeValueChange,
  ECTab,
  ModuleTabForQuestionnaire,
  ScoreMapEntry,
  SubcriteriaRenderer,
  SubcriteriaTab,
  TemplateData,
} from '../provider-questionnaire.types';
import {isEC3} from '../../../constants/evaluation-criteria.constants';
import {ProgressDashboardComponent} from '../../progress-dashboard/progress-dashboard.component';
import {NoDataComponent} from '../../no-data/no-data.component';
import {toSignal} from '@angular/core/rxjs-interop';
import {AttributesByEvaluationCriteriaMap} from '../../subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {LoadingComponent} from '../../loading/loading.component';
import {TabStatusDirective} from '../../../tabs/tab-status.directive';
import {TabsUnsavedRevealDirective} from '../../../tabs/tabs-unsaved-reveal.directive';
import {OptionsSelectComponent} from '../../inputs/options-select/options-select.component';
import {SelectOption, SelectValue} from '../../inputs/options-select/options-select.types';
import {TemplateSuggestionStore} from '../suggestion-review/template-suggestion.store';
import {isAiTemplateAnsweringAvailableFor} from '../suggestion-review/ai-template-answering-availability';
import {
  PersistedAttributeValue,
  SUGGESTION_KEY_PREFIX,
  SuggestionListFilter,
} from '../suggestion-review/template-suggestion.types';
import {TenderStatusUtils} from '../../../../phase/tender-status.util';
import {TemplateFillDialogComponent} from '../template-fill/template-fill-dialog.component';
import {applyTemplateFill, summarizeTemplateFill} from '../template-fill/template-fill.calc';
import {TemplateFillSummary} from '../template-fill/template-fill.types';

export enum QuestionnaireMode {
  QUESTIONNAIRE = 'questionnaire',
  TEMPLATE = 'template',
}

@Component({
  selector: 'app-generic-questionnaire',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoPipe,
    Tab,
    TabList,
    Tabs,
    TabPanels,
    TabPanel,
    QuestionnaireHeaderComponent,
    QuestionnaireEc3TabsComponent,
    QuestionnaireStandardTabsComponent,
    ProgressDashboardComponent,
    NoDataComponent,
    TranslateDirectusPipe,
    LoadingComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
    OptionsSelectComponent,
    Button,
    TemplateFillDialogComponent,
  ],
  providers: [TemplateSuggestionStore],
  templateUrl: './generic-questionnaire.component.html',
})
export class GenericQuestionnaireComponent {
  protected readonly TenderPhaseType = TenderPhaseType;
  protected readonly isEC3 = isEC3;
  protected readonly suggestionFilterOptions: SelectOption<string>[] = Object.values(
    SuggestionListFilter,
  ).map((filter) => ({directusKey: `${SUGGESTION_KEY_PREFIX}.filter.${filter}`, value: filter}));

  mode = input<QuestionnaireMode>(QuestionnaireMode.QUESTIONNAIRE);
  tenderId = input<number | undefined>();
  phaseType = input.required<TenderPhaseType>();
  tenderStatus = input<TenderStatus | undefined>();
  tenderDetails = input<TenderDetailResponse>(); // cant be Mandatory, some case don't have it
  selectedTool = input<ProviderToolResponse | undefined>();
  supportedModuleFunctionIds = input<number[] | undefined>(undefined);
  reloadKey = input(0);
  showTenderProjectReferences = input<boolean>(false);

  protected readonly projectReferencesToolId = computed<number | undefined>(() =>
    this.showTenderProjectReferences() ? this.tenderDetails()?.toolId : this.selectedTool()?.id,
  );

  protected readonly projectReferenceScores = computed<ProjectReferenceScoreView[]>(() =>
    this.showTenderProjectReferences() ? (this.tenderDetails()?.projectReferenceScores ?? []) : [],
  );

  pieChartLabel = computed<string | undefined>(() => {
    if (this.phaseType() === TenderPhaseType.RFI) {
      return 'questionnaire.progress.dashboard.rating.distribution.pie-chart.rfi.label';
    }
    return undefined;
  });

  showProgressDashboard = computed<boolean>(() => {
    // Is it possible to show any data if not FINISHED?
    return (
      this.tenderStatus() !== TenderStatus.EXPIRED && this.tenderStatus() !== TenderStatus.REJECTED
    );
  });

  readonly = input<boolean>(false);
  providerScores = input<ProviderScoresResponse | undefined>();

  tabStatusChange = output<TabStatusEnum>();
  sent = output<void>();

  protected readonly confirmationService = inject(ConfirmationService);
  protected readonly questionnaireService = inject(QuestionnaireService);
  protected readonly toast = inject(ToastService);
  protected readonly suggestionStore = inject(TemplateSuggestionStore);
  private readonly badgeListService = inject(SubcriteriaBadgeService);

  private readonly directusService = inject(DirectusService);

  protected readonly loading = signal(false);
  protected readonly TabStatusEnum = TabStatusEnum;
  protected readonly QuestionnaireMode = QuestionnaireMode;

  private readonly tabManager = inject(TabManagementService);

  protected readonly ecTabs = signal<ECTab[]>([]);

  private readonly savedProjectReferenceCount = linkedSignal<number>(
    () => this.projectReferenceScores().length,
  );
  private readonly hasUnsavedProjectReferences = signal<boolean>(false);
  protected readonly activeECTabIndex = signal(0);
  protected readonly activeScTabIndex = signal(0);
  protected readonly activeModuleTabIndex = signal(0);
  protected readonly activeModuleScTabIndex = signal(0);

  // -----------------------------
  // Dashboard inputs (reused)
  // -----------------------------

  private readonly allSubCriteria = toSignal(this.directusService.loadSubCriteria(), {
    initialValue: [] as EvaluationSubCriteriaDirectus[],
  });

  // Add: load Directus evaluation criteria (safe for providers)
  private readonly allDirectusEvaluationCriteria = toSignal(
    this.directusService.loadEvaluationCriteria(),
    {initialValue: [] as EvaluationCriteria[]},
  );

  /**
   * Dashboard needs EvaluationCriteria translations to label the RFP (EC-based) bar chart/table.
   * Provider-side: derive from Directus + ecTabs (NO customer REST).
   */
  protected readonly dashboardEvaluationCriteria = computed<EvaluationCriteria[]>(() => {
    const all = this.allDirectusEvaluationCriteria() ?? [];
    const tabDirectusIds = new Set((this.ecTabs() ?? []).map((t) => t.directusId).filter(Boolean));

    const fromDirectus =
      tabDirectusIds.size > 0 ? all.filter((ec) => tabDirectusIds.has(ec.id)) : [];

    if (fromDirectus.length) return fromDirectus;

    // Fallback: build minimal objects from ecTabs translations + ecNumber
    return (this.ecTabs() ?? []).map((ecTab) => {
      return {
        id: ecTab.directusId, // uuid string
        ecNumber: ecTab.ecNumber,
        name: ecTab.title,
        subcriteria: [],
        translations: ecTab.translations ?? [],
      } as unknown as EvaluationCriteria;
    });
  });

  private mapProviderScoresToAttributesByEvaluationCriteriaMap(
    providerScores: ProviderScoresResponse,
  ): AttributesByEvaluationCriteriaMap {
    const result: AttributesByEvaluationCriteriaMap = {};
    const meta = this.directusAttributeMeta();

    for (const ec of providerScores.evaluationCriteria ?? []) {
      const ecId = ec.id;
      result[ecId] ??= {};

      for (const sc of ec.subCriteria ?? []) {
        const scId = sc.id;
        result[ecId][scId] ??= [];

        for (const attr of sc.attributes ?? []) {
          const directusMeta = meta.get(attr.directusId);

          result[ecId][scId].push({
            id: attr.id,
            ecNumber: ec.ecNumber,
            scNumber: sc.scNumber,
            directusId: attr.directusId,
            title: directusMeta?.title ?? attr.directusId,
            translations: directusMeta?.translations ?? [],
            scaNumber: attr.scaNumber ?? directusMeta?.scaNumber ?? 0,
            selectedImportanceScore: attr.importanceScores?.[0]?.importanceScore ? 'done' : 'open',
            isCompleted: attr.scores !== undefined,
            hasChanges: false,
            isSaving: false,
          });
        }
      }
    }

    return result;
  }

  private mapEcTabsToAttributesByEvaluationCriteriaMap(
    ecTabs: ECTab[],
  ): AttributesByEvaluationCriteriaMap {
    const result: AttributesByEvaluationCriteriaMap = {};
    const meta = this.directusAttributeMeta();

    const scNumberByDirectusId = new Map<string, number>();
    for (const sc of this.allSubCriteria() ?? []) {
      scNumberByDirectusId.set(sc.id, sc.sc_number);
    }

    for (const ecTab of ecTabs ?? []) {
      const ecId = ecTab.id;
      result[ecId] ??= {};

      const allSubTabs: SubcriteriaTab[] =
        isEC3(ecTab.ecNumber) && ecTab.moduleTabs
          ? ecTab.moduleTabs.flatMap((m) => m.subcriteriaTabs ?? [])
          : (ecTab.subcriteriaTabs ?? []);

      for (const scTab of allSubTabs) {
        const scTabId = scTab.id;
        result[ecId][scTabId] ??= [];

        // Don't fall back to 0. If we can't derive it yet, keep it undefined.
        const scNumber =
          (scTab as unknown as {scNumber?: number}).scNumber ??
          (scTab.directusId ? scNumberByDirectusId.get(scTab.directusId) : undefined);

        for (const attr of scTab.attributes ?? []) {
          const completed = this.isAttributeCompleted(attr);
          const directusMeta = meta.get((attr as unknown as {directusId: string}).directusId);

          result[ecId][scTabId].push({
            id: (attr as unknown as {attributeId?: number}).attributeId ?? 0,
            ecNumber: ecTab.ecNumber,
            scNumber,
            scaNumber: (attr as unknown as {scaNumber?: number}).scaNumber ?? 0,
            directusId: (attr as unknown as {directusId: string}).directusId,
            title: directusMeta?.title || '',
            translations: directusMeta?.translations || [],
            isCompleted: completed,
            selectedImportanceScore: this.convertScore(attr),
            hasChanges: false,
            isSaving: false,
          });
        }
      }
    }

    return result;
  }

  private convertScore(attr: {
    fulfillmentType?: FulfillmentType | undefined;
    score?: number;
    directusId?: string;
  }) {
    let convertedScore: any = attr.score;

    switch (attr.fulfillmentType) {
      case FulfillmentType.TERNARY:
        if (attr.score === 0) {
          convertedScore = 'none';
        }
        if (attr.score === 0.5) {
          convertedScore = 'partly';
        }
        if (attr.score === 1) {
          convertedScore = 'full';
        }
        if (attr.score == undefined) {
          convertedScore = attr.score;
        }
        break;
      default:
        convertedScore = attr.score !== undefined ? 'done' : 'open';
        break;
    }
    return convertedScore;
  }

  /**
   * ProviderScores label enrichment cache (Directus attribute translations).
   * Key = attribute directusId.
   */
  private readonly directusAttributeMeta = signal<
    Map<string, {translations: DirectusTranslation[]; scaNumber: number; title: string}>
  >(new Map());

  private async loadDirectusAttributeMetaForProviderScores(
    providerScores: ProviderScoresResponse,
  ): Promise<void> {
    const neededSubCriteriaDirectusIds = new Set<string>();

    for (const ec of providerScores.evaluationCriteria ?? []) {
      for (const sc of ec.subCriteria ?? []) {
        if (sc.directusId) neededSubCriteriaDirectusIds.add(sc.directusId);
      }
    }

    this.loadDirectusAttributeMeta(neededSubCriteriaDirectusIds);
  }

  private async loadDirectusAttributeMetaByEcTabs(ecTabs: ECTab[]): Promise<void> {
    const neededSubCriteriaDirectusIds = new Set<string>();
    ecTabs.forEach((ecTab) => {
      ecTab.subcriteriaTabs.map((scTab) => neededSubCriteriaDirectusIds.add(scTab.directusId));
    });

    this.loadDirectusAttributeMeta(neededSubCriteriaDirectusIds);
  }

  private async loadDirectusAttributeMeta(
    neededSubCriteriaDirectusIds: Set<string>,
  ): Promise<void> {
    const meta = new Map<
      string,
      {translations: DirectusTranslation[]; scaNumber: number; title: string}
    >();

    for (const subCriteriaDirectusId of neededSubCriteriaDirectusIds) {
      const attrs =
        await this.directusService.loadSubCriteriaAttributesBySubCriteria(subCriteriaDirectusId);

      for (const a of attrs ?? []) {
        meta.set(a.id, {
          translations: a.translations,
          scaNumber: a.sca_number,
          title: a.id, // fallback; UI typically prefers translations anyway
        });
      }
    }

    this.directusAttributeMeta.set(meta);
  }

  readonly questionnaireCompletionStatus = computed<TabStatusEnum>(() => {
    const tabStatuses = this.ecTabCompletionStatus();
    return this.questionnaireService.calculateOverallCompletionStatus(tabStatuses);
  });

  protected readonly ecTabCompletionStatus = computed((): TabStatusEnum[] => {
    return this.ecTabs().map((ecTab) => {
      if (isEC3(ecTab.ecNumber) && ecTab.moduleTabs) {
        return this.calculateEC3TabStatus(ecTab.moduleTabs);
      }

      if (ecTab.subcriteriaTabs?.length) {
        const subcriteriaStatuses = this.questionnaireService.calculateTabCompletionStatus(
          ecTab.subcriteriaTabs,
          this.savedProjectReferenceCount(),
        );
        return this.questionnaireService.calculateOverallCompletionStatus(subcriteriaStatuses);
      }

      return TabStatusEnum.NOT_STARTED;
    });
  });

  protected readonly ecTabsUnsaved = computed<boolean[]>(() => {
    return this.ecTabs().map((ecTab) => {
      if (isEC3(ecTab.ecNumber) && ecTab.moduleTabs) {
        return ecTab.moduleTabs.some((moduleTab) =>
          this.hasUnsavedAttributes(moduleTab.subcriteriaTabs),
        );
      }

      return this.hasUnsavedAttributes(ecTab.subcriteriaTabs);
    });
  });

  private calculateEC3TabStatus(moduleTabs: ModuleTabForQuestionnaire[]): TabStatusEnum {
    const allSubcriteriaStatuses: TabStatusEnum[] = [];

    for (const moduleTab of moduleTabs) {
      if (moduleTab.subcriteriaTabs?.length) {
        const moduleStatuses = this.questionnaireService.calculateTabCompletionStatus(
          moduleTab.subcriteriaTabs,
          this.savedProjectReferenceCount(),
        );
        allSubcriteriaStatuses.push(...moduleStatuses);
      }
    }

    return this.questionnaireService.calculateOverallCompletionStatus(allSubcriteriaStatuses);
  }

  private hasUnsavedAttributes(subcriteriaTabs: SubcriteriaTab[] | undefined): boolean {
    return this.mapUnsavedAttributes(subcriteriaTabs).some((hasChanges) => hasChanges);
  }

  private mapUnsavedAttributes(subcriteriaTabs: SubcriteriaTab[] | undefined): boolean[] {
    return (subcriteriaTabs ?? []).map((subcriteriaTab) =>
      SubcriteriaRenderer.PROJECT_REFERENCES === subcriteriaTab.renderer
        ? this.hasUnsavedProjectReferences()
        : subcriteriaTab.attributes.some((attribute) => attribute.hasChanges),
    );
  }

  protected onProjectReferencesSavedCountChange(savedCount: number): void {
    this.savedProjectReferenceCount.set(savedCount);
  }

  protected onProjectReferencesUnsavedChange(hasUnsaved: boolean): void {
    this.hasUnsavedProjectReferences.set(hasUnsaved);
  }

  protected readonly currentECTab = computed(() => {
    const tabs = this.ecTabs();
    const index = this.activeECTabIndex();
    return tabs[index] || null;
  });

  protected readonly currentScTab = computed(() => {
    if (this.isCurrentECTabEC3()) {
      return this.tabManager.getCurrentEC3SubTab(
        this.ecTabs(),
        this.activeECTabIndex(),
        this.activeModuleTabIndex(),
        this.activeModuleScTabIndex(),
      );
    } else {
      return this.tabManager.getCurrentStandardSubTab(
        this.ecTabs(),
        this.activeECTabIndex(),
        this.activeScTabIndex(),
      );
    }
  });

  protected readonly currentModuleTab = computed(() => {
    return this.tabManager.getCurrentEC3ModuleTab(
      this.ecTabs(),
      this.activeECTabIndex(),
      this.activeModuleTabIndex(),
    );
  });

  readonly currentTabCanSave = computed(() => {
    const tab = this.currentScTab();
    if (!tab) return false;
    const hasChanges = tab.attributes.some((attr) => attr.hasChanges);
    return hasChanges && !tab.isSaving;
  });

  readonly currentTabIsSaving = computed(() => this.currentScTab()?.isSaving || false);

  protected readonly isCurrentECTabEC3 = computed(() => {
    const tab = this.currentECTab();
    return tab ? isEC3(tab.ecNumber) : false;
  });

  protected readonly currentScTabCompletionStatus = computed((): TabStatusEnum[] => {
    const ecTab = this.currentECTab();
    if (!ecTab) return [];

    if (isEC3(ecTab.ecNumber) && ecTab.moduleTabs) {
      const currentModuleTab = this.currentModuleTab();
      return currentModuleTab?.subcriteriaTabs
        ? this.questionnaireService.calculateTabCompletionStatus(
            currentModuleTab.subcriteriaTabs,
            this.savedProjectReferenceCount(),
          )
        : [];
    }

    return ecTab.subcriteriaTabs
      ? this.questionnaireService.calculateTabCompletionStatus(
          ecTab.subcriteriaTabs,
          this.savedProjectReferenceCount(),
        )
      : [];
  });

  protected readonly currentScTabsUnsaved = computed<boolean[]>(() => {
    const ecTab = this.currentECTab();
    if (!ecTab) return [];

    if (isEC3(ecTab.ecNumber) && ecTab.moduleTabs) {
      return this.mapUnsavedAttributes(this.currentModuleTab()?.subcriteriaTabs);
    }

    return this.mapUnsavedAttributes(ecTab.subcriteriaTabs);
  });

  protected readonly currentModuleTabCompletionStatus = computed((): TabStatusEnum[] => {
    const ecTab = this.currentECTab();
    if (!ecTab || !isEC3(ecTab.ecNumber) || !ecTab.moduleTabs) return [];

    return ecTab.moduleTabs.map((moduleTab) => {
      if (!moduleTab.subcriteriaTabs?.length) return TabStatusEnum.NOT_STARTED;

      const subcriteriaStatuses = this.questionnaireService.calculateTabCompletionStatus(
        moduleTab.subcriteriaTabs,
        this.savedProjectReferenceCount(),
      );
      return this.questionnaireService.calculateOverallCompletionStatus(subcriteriaStatuses);
    });
  });

  protected readonly currentModuleTabsUnsaved = computed<boolean[]>(() => {
    const ecTab = this.currentECTab();
    if (!ecTab || !isEC3(ecTab.ecNumber) || !ecTab.moduleTabs) return [];

    return ecTab.moduleTabs.map((moduleTab) =>
      this.hasUnsavedAttributes(moduleTab.subcriteriaTabs),
    );
  });

  protected readonly isFinishing = signal(false);
  readonly canFinishQuestionnaire = computed(() => {
    return this.questionnaireCompletionStatus() === TabStatusEnum.COMPLETED && !this.isFinishing();
  });

  readonly currentTabBadgeListItems = computed((): BadgeListItem[] => {
    const tab = this.currentScTab();
    if (!tab) return [];

    return this.badgeListService.createBadgeListItems(tab.attributes, {
      includeAttributeType: true,
      sortByScaNumber: false,
    });
  });

  protected readonly showHeader = computed(
    () => this.mode() === QuestionnaireMode.QUESTIONNAIRE && !this.readonly(),
  );

  protected readonly showsTemplateFill = computed(
    () =>
      this.showHeader() &&
      !!this.tenderDetails()?.toolId &&
      TenderStatusUtils.canEditQuestionnaire(this.tenderDetails()?.status),
  );
  protected templateFillDialogVisible = signal(false);
  protected readonly templateFillSummary = signal<TemplateFillSummary>({
    fillableCount: 0,
    answeredCount: 0,
  });
  protected readonly loadingTemplateFill = signal(false);
  protected readonly fillingFromTemplate = signal(false);
  private readonly templateScoreMap = signal(new Map<string, ScoreMapEntry>());

  protected readonly showTemplateTitle = computed(() => this.mode() === QuestionnaireMode.TEMPLATE);

  protected readonly templateTitleKey = computed(
    () => `provider.templates.${this.phaseType().toLowerCase()}`,
  );

  protected readonly bulkAccepting = signal(false);
  protected readonly showsSuggestionToolbar = computed(
    () =>
      isAiTemplateAnsweringAvailableFor(this.phaseType()) &&
      this.mode() === QuestionnaireMode.TEMPLATE &&
      !this.readonly(),
  );
  private readonly currentGroupAttributeIds = computed<ReadonlySet<string>>(() => {
    const ecTab = this.currentECTab();
    if (!ecTab) return new Set();
    const subcriteriaTabs = [
      ...(ecTab.subcriteriaTabs ?? []),
      ...(ecTab.moduleTabs ?? []).flatMap((moduleTab) => moduleTab.subcriteriaTabs ?? []),
    ];
    return new Set(
      subcriteriaTabs.flatMap((tab) => tab.attributes.map((attribute) => attribute.directusId)),
    );
  });
  private readonly bulkAcceptableSuggestions = computed(() =>
    this.suggestionStore.highConfidenceOpenSuggestionsAmong(this.currentGroupAttributeIds()),
  );
  protected readonly bulkAcceptCount = computed(() => this.bulkAcceptableSuggestions().length);
  protected readonly showsBulkAccept = computed(
    () => this.showsSuggestionToolbar() && this.bulkAcceptCount() > 0,
  );

  /**
   * ProgressDashboard completion uses `selectedImportanceScore !== undefined`.
   *
   * We support BOTH questionnaire loading modes:
   *  - readonly + providerScores(): use providerScores as source of truth
   *  - otherwise: use ecTabs() as source of truth
   */
  protected readonly dashboardAttributesByEvaluationCriteriaMap = computed(() => {
    const ps = this.providerScores();
    if (this.readonly() && ps) {
      return this.mapProviderScoresToAttributesByEvaluationCriteriaMap(ps);
    }
    return this.mapEcTabsToAttributesByEvaluationCriteriaMap(this.ecTabs());
  });
  /**
   * Sub-criteria list specifically for the ProgressDashboard tables.
   * We derive it from the actual data source (providerScores or ecTabs),
   * not from evaluationCriteriaData (which may be unset in provider/template flows).
   */
  protected readonly dashboardEvaluationSubCriteria = computed<EvaluationSubCriteriaDirectus[]>(
    () => {
      const all = this.allSubCriteria();

      const ps = this.providerScores();
      if (this.readonly() && ps) {
        const directusIds = new Set<string>();
        for (const ec of ps.evaluationCriteria ?? []) {
          for (const sc of ec.subCriteria ?? []) {
            if (sc.directusId) directusIds.add(sc.directusId);
          }
        }
        return all.filter((sc) => directusIds.has(sc.id));
      }

      const tabs = this.ecTabs();
      const directusIds = new Set<string>();

      for (const ecTab of tabs ?? []) {
        const subTabs: SubcriteriaTab[] =
          isEC3(ecTab.ecNumber) && ecTab.moduleTabs
            ? ecTab.moduleTabs.flatMap((m) => m.subcriteriaTabs ?? [])
            : (ecTab.subcriteriaTabs ?? []);

        for (const scTab of subTabs) {
          if (scTab.directusId) directusIds.add(scTab.directusId);
        }
      }

      return all.filter((sc) => directusIds.has(sc.id));
    },
  );

  constructor() {
    effect(() => {
      this.reloadKey();
      void this.loadQuestionnaireData();
    });

    effect(() => {
      const status = this.questionnaireCompletionStatus();
      this.tabStatusChange.emit(status);
    });

    effect(() => {
      const persisted = this.suggestionStore.lastPersistedValue();
      if (persisted) {
        untracked(() => this.applyPersistedValue(persisted));
      }
    });
  }

  protected onSuggestionFilterChange(value: SelectValue<string>): void {
    const filter = Object.values(SuggestionListFilter).find((candidate) => candidate === value);
    this.suggestionStore.filter.set(filter ?? SuggestionListFilter.ALL);
  }

  protected onBulkAccept(): void {
    const count = this.bulkAcceptCount();
    this.confirmationService.confirm({
      header: translate(`${SUGGESTION_KEY_PREFIX}.bulk.confirm.header`),
      message: translate(`${SUGGESTION_KEY_PREFIX}.bulk.confirm.message`, {count}),
      icon: 'pi pi-sparkles',
      accept: () => void this.runBulkAccept(),
    });
  }

  private async runBulkAccept(): Promise<void> {
    const suggestions = this.bulkAcceptableSuggestions();
    this.bulkAccepting.set(true);
    try {
      const result = await this.suggestionStore.acceptAll(suggestions);
      await this.toast.showSuccess(
        `${SUGGESTION_KEY_PREFIX}.bulk.result.header`,
        `${SUGGESTION_KEY_PREFIX}.bulk.result.detail`,
        {accepted: result.accepted, skipped: result.skipped},
      );
    } finally {
      this.bulkAccepting.set(false);
    }
  }

  private applyPersistedValue(value: PersistedAttributeValue): void {
    this.ecTabs.set(this.tabManager.applyPersistedAttributeValue(this.ecTabs(), value));
  }

  /**
   * Decide whether an attribute is completed in the "generic questionnaire" sense.
   * Supports various shapes: score (number/percent), value (enum/string/boolean), selected (boolean).
   */
  private isAttributeCompleted(attr: unknown): boolean {
    const a = attr as {
      score?: unknown;
      value?: unknown;
      selected?: unknown;
    };
    return a.score !== undefined && a.score !== null;
  }

  private async loadQuestionnaireData(): Promise<void> {
    this.loading.set(true);
    this.resetAllTabNavigation();
    try {
      if (this.readonly() && this.providerScores()) {
        await this.loadReadonlyData();
      } else if (this.mode() === QuestionnaireMode.TEMPLATE) {
        await this.loadTemplateData();
      } else {
        await this.loadQuestionnaireEditableData();
      }
    } catch {
      this.ecTabs.set([]);
    } finally {
      this.loading.set(false);
    }
    await this.loadSuggestions();
  }

  private async loadSuggestions(): Promise<void> {
    const tool = this.selectedTool();
    if (this.mode() !== QuestionnaireMode.TEMPLATE || !tool) return;
    if (!isAiTemplateAnsweringAvailableFor(this.phaseType())) {
      this.suggestionStore.clear();
      return;
    }
    await this.suggestionStore.load(tool.id, this.phaseType());
  }

  private async loadReadonlyData(): Promise<void> {
    const providerScores = this.providerScores();
    if (!providerScores) return;

    await this.loadDirectusAttributeMetaForProviderScores(providerScores);

    const ecTabs = await this.questionnaireService.transformProviderScoresToECTabs(providerScores);
    this.ecTabs.set(ecTabs);
  }

  private async loadTemplateData(): Promise<void> {
    const tool = this.selectedTool();
    if (!tool) return;

    const supportedModuleFunctionIds = this.supportedModuleFunctionIds();
    const templateData: TemplateData = await this.questionnaireService.loadTemplateData(
      tool.id,
      this.phaseType(),
      supportedModuleFunctionIds,
    );
    await this.loadDirectusAttributeMetaByEcTabs(templateData.ecTabs);
    this.ecTabs.set(templateData.ecTabs);
    this.savedProjectReferenceCount.set(templateData.projectReferenceCount);
  }

  private async loadQuestionnaireEditableData(): Promise<void> {
    const tenderId = this.tenderId();
    if (!tenderId) return;

    const tabs = await this.questionnaireService.loadQuestionnaireData(tenderId, this.phaseType());
    this.ecTabs.set(tabs);
  }

  protected async onFillFromTemplateClick(): Promise<void> {
    const toolId = this.tenderDetails()?.toolId;
    if (!toolId) return;

    this.loadingTemplateFill.set(true);
    try {
      const scoreMap = await this.questionnaireService.loadTemplateScoreMap(
        toolId,
        this.phaseType(),
      );
      const summary = summarizeTemplateFill(this.ecTabs(), scoreMap);
      if (summary.fillableCount + summary.answeredCount === 0) {
        await this.toast.showWarning(
          'provider.questionnaire.templateFill.empty.header',
          'provider.questionnaire.templateFill.empty.detail',
        );
        return;
      }
      this.templateScoreMap.set(scoreMap);
      this.templateFillSummary.set(summary);
      this.templateFillDialogVisible.set(true);
    } finally {
      this.loadingTemplateFill.set(false);
    }
  }

  protected async onTemplateFillConfirmed(overwriteAnswered: boolean): Promise<void> {
    const tenderId = this.tenderId();
    if (!tenderId) return;

    const result = applyTemplateFill(this.ecTabs(), this.templateScoreMap(), overwriteAnswered);
    this.fillingFromTemplate.set(true);
    try {
      await this.questionnaireService.submitAttributeScores(
        tenderId,
        this.phaseType(),
        result.filledAttributes,
      );
      this.ecTabs.set(result.ecTabs);
      this.templateFillDialogVisible.set(false);
      await this.toast.showSuccess(
        'provider.questionnaire.templateFill.success.header',
        'provider.questionnaire.templateFill.success.detail',
        {count: result.filledAttributes.length},
      );
    } finally {
      this.fillingFromTemplate.set(false);
    }
  }

  onAttributeChange(change: AttributeValueChange): void {
    this.applyAttributePatch(change);
  }

  async saveCurrentTabAttributes(): Promise<void> {
    const currentTab = this.currentScTab();
    const currentECTab = this.currentECTab();
    if (!currentTab || !currentECTab || !this.currentTabCanSave()) return;

    this.setSavingState(currentECTab, currentTab, true);

    try {
      await this.saveAttributes(currentTab);
      this.resetChangesAfterSave(currentECTab, currentTab);
      await this.toast.showSuccess('success.attributesSaved');
    } finally {
      this.setSavingState(currentECTab, currentTab, false);
    }
  }

  private applyAttributePatch(change: AttributeValueChange): void {
    const currentSubcriteriaTab = this.currentScTab();
    const currentECTab = this.currentECTab();
    if (!currentSubcriteriaTab || !currentECTab) return;

    let updatedECTabs;
    if (this.isCurrentECTabEC3()) {
      const currentModuleTab = this.currentModuleTab();
      if (!currentModuleTab) return;

      updatedECTabs = this.tabManager.handleEC3AttributeChange(
        change,
        this.ecTabs(),
        currentECTab.id,
        currentModuleTab.moduleId,
        currentSubcriteriaTab.id,
      );
    } else {
      updatedECTabs = this.tabManager.handleStandardECAttributeChange(
        change,
        this.ecTabs(),
        currentECTab.id,
        currentSubcriteriaTab.id,
      );
    }
    this.ecTabs.set(updatedECTabs);
  }

  private setSavingState(currentECTab: ECTab, currentTab: SubcriteriaTab, isSaving: boolean): void {
    if (this.isCurrentECTabEC3()) {
      const currentModuleTab = this.currentModuleTab();
      if (!currentModuleTab) return;

      const updatedTabs = this.tabManager.setEC3SavingState(
        this.ecTabs(),
        currentECTab.id,
        currentModuleTab.moduleId,
        currentTab.id,
        isSaving,
      );
      this.ecTabs.set(updatedTabs);
    } else {
      const updatedTabs = this.tabManager.setStandardECSavingState(
        this.ecTabs(),
        currentECTab.id,
        currentTab.id,
        isSaving,
      );
      this.ecTabs.set(updatedTabs);
    }
  }

  private async saveAttributes(currentTab: SubcriteriaTab): Promise<void> {
    if (this.mode() === QuestionnaireMode.TEMPLATE) {
      const tool = this.selectedTool();
      if (!tool) return;
      await this.questionnaireService.saveTemplateScores(
        tool.id,
        this.phaseType(),
        currentTab.attributes,
      );
    } else {
      const tenderId = this.tenderId();
      if (!tenderId) return;
      await this.questionnaireService.saveTabAttributes(
        tenderId,
        this.phaseType(),
        currentTab.attributes,
      );
    }
  }

  private resetChangesAfterSave(currentECTab: ECTab, currentTab: SubcriteriaTab): void {
    if (this.isCurrentECTabEC3()) {
      const currentModuleTab = this.currentModuleTab();
      if (!currentModuleTab) return;

      const tabsWithResetChanges = this.tabManager.resetEC3Changes(
        this.ecTabs(),
        currentECTab.id,
        currentModuleTab.moduleId,
        currentTab.id,
      );
      this.ecTabs.set(tabsWithResetChanges);
    } else {
      const tabsWithResetChanges = this.tabManager.resetStandardECChanges(
        this.ecTabs(),
        currentECTab.id,
        currentTab.id,
      );
      this.ecTabs.set(tabsWithResetChanges);
    }
  }

  onECTabChange(index: number): void {
    this.activeECTabIndex.set(index);
    this.resetAllSubNavigation();

    logTabRender('S', index);
  }

  onModuleTabChange(index: number): void {
    this.activeModuleTabIndex.set(index);
    this.activeModuleScTabIndex.set(0);

    logTabRender('M', index);
  }

  onModuleScTabChange(index: number): void {
    this.activeModuleScTabIndex.set(index);

    logTabRender('SC', index);
  }

  onScTabChange(index: number): void {
    this.activeScTabIndex.set(index);

    logTabRender('SC', index);
  }

  private resetAllTabNavigation(): void {
    this.activeECTabIndex.set(0);
    this.resetAllSubNavigation();
  }

  private resetAllSubNavigation(): void {
    this.activeScTabIndex.set(0);
    this.activeModuleTabIndex.set(0);
    this.activeModuleScTabIndex.set(0);
  }

  onFinishQuestionnaireClick(): void {
    this.questionnaireService.showFinishConfirmation(
      this.phaseType(),
      this.confirmationService,
      () => this.finishQuestionnaire(),
    );
  }

  private async finishQuestionnaire(): Promise<void> {
    const tenderId = this.tenderId();
    if (!tenderId) return;

    this.isFinishing.set(true);

    try {
      await this.questionnaireService.finishQuestionnaireForPhase(tenderId, this.phaseType());

      const successKey = 'success.questionnaireCompleted';
      const detailKey = 'success.questionnaireCompletedDetail';

      await this.toast.showSuccess(successKey, detailKey);
      this.sent.emit();
    } finally {
      this.isFinishing.set(false);
    }
  }
}
